import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Table, Alert } from 'react-bootstrap';
import { useBills } from '../contexts/BillsContext';
import { useAppData } from '../../../providers/AppDataProvider';
import { Bill } from '../../../types/accounting';

interface LineItemRow {
  id: string;
  description: string;
  quantity: string;
  unitAmount: string;
}

interface CreateBillModalProps {
  show: boolean;
  onHide: () => void;
  editBill?: Bill | null;
}

// Adelaide-local 'YYYY-MM-DD' — toISOString() is UTC, so in the evening it
// reads a day ahead of the local business date (audit: medium).
const adelaideToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Adelaide' });
const addDaysStr = (dateStr: string, days: number) => {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const today = adelaideToday();
const defaultDueDate = addDaysStr(today, 30);

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

export function CreateBillModal({ show, onHide, editBill }: CreateBillModalProps) {
  const { createBill, createBillLoading, updateBill, updateBillLoading } = useBills();
  const { customers } = useAppData();
  const isEditMode = !!editBill;

  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [billDate, setBillDate] = useState(today);
  const [dueDate, setDueDate] = useState(defaultDueDate);
  const [reference, setReference] = useState('');
  const [lineItems, setLineItems] = useState<LineItemRow[]>([
    { id: generateId(), description: '', quantity: '1', unitAmount: '' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const customerOptions = (customers || []).filter(c => c.id && (c.name || c.company));

  useEffect(() => {
    if (!show) return;
    setError(null);
    setSuccess(null);
    if (editBill) {
      setBillDate(editBill.date || today);
      setDueDate(editBill.dueDate || defaultDueDate);
      setReference(editBill.reference || '');
      setSelectedContactId(editBill.contactId || '');
      if (editBill.lineItems && editBill.lineItems.length > 0) {
        setLineItems(
          editBill.lineItems.map(li => ({
            id: generateId(),
            description: li.description || '',
            quantity: String(li.quantity ?? 1),
            unitAmount: String(li.unitAmount ?? 0),
          }))
        );
      } else {
        setLineItems([{ id: generateId(), description: '', quantity: '1', unitAmount: '' }]);
      }
    } else {
      setSelectedContactId('');
      setBillDate(today);
      setDueDate(defaultDueDate);
      setReference('');
      setLineItems([{ id: generateId(), description: '', quantity: '1', unitAmount: '' }]);
    }
  }, [show, editBill]);

  const addLineItem = () => {
    setLineItems(prev => [...prev, { id: generateId(), description: '', quantity: '1', unitAmount: '' }]);
  };

  const removeLineItem = (id: string) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  const updateLineItem = (id: string, field: keyof LineItemRow, value: string) => {
    setLineItems(prev =>
      prev.map(item => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  // Mirror the server's per-line, integer-cents GST math so the preview total
  // equals what gets saved (bills are GST-exclusive @ 10%) — previously the
  // preview showed the ex-GST subtotal as "Total", understating it (audit: high).
  const computeTotals = () => {
    let subC = 0;
    let gstC = 0;
    for (const item of lineItems) {
      const qty = parseFloat(item.quantity) || 0;
      const unit = parseFloat(item.unitAmount) || 0;
      const rawC = Math.round(qty * unit * 100);
      subC += rawC;
      gstC += Math.round(rawC * 0.1);
    }
    return { subtotal: subC / 100, gst: gstC / 100, total: (subC + gstC) / 100 };
  };

  const validate = (): string | null => {
    if (!isEditMode && !selectedContactId) return 'Please select a supplier.';
    if (lineItems.length === 0) return 'At least one line item is required.';
    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item.description.trim()) return `Line item ${i + 1} requires a description.`;
      const qty = parseFloat(item.quantity);
      if (isNaN(qty) || qty <= 0) return `Line item ${i + 1} requires a valid quantity.`;
      const unit = parseFloat(item.unitAmount);
      if (isNaN(unit)) return `Line item ${i + 1} requires a valid unit amount.`;
    }
    return null;
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const contact = customerOptions.find(c => c.id === selectedContactId);
    const contactName = contact ? (contact.name || contact.company || '') : '';
    const contactEmail = contact?.email ?? undefined;

    const lineItemsPayload = lineItems.map(item => ({
      description: item.description.trim(),
      quantity: parseFloat(item.quantity),
      unitAmount: parseFloat(item.unitAmount),
      taxType: 'GST' as const,
    }));

    try {
      if (isEditMode && editBill) {
        const result = await updateBill({
          billId: editBill.id,
          date: billDate,
          dueDate,
          reference: reference.trim() || undefined,
          lineItems: lineItemsPayload,
        });
        setSuccess(`Bill ${result.bill.billNumber} updated.`);
        setTimeout(() => onHide(), 1500);
      } else {
        const result = await createBill({
          contactId: selectedContactId,
          contactName,
          contactEmail,
          reference: reference.trim() || undefined,
          date: billDate,
          dueDate,
          lineItems: lineItemsPayload,
        });
        setSuccess(`Bill ${result.bill.billNumber} created.`);
        setTimeout(() => onHide(), 1500);
      }
    } catch (e: any) {
      setError(e?.message || (isEditMode ? 'Update failed.' : 'Create failed.'));
    }
  };

  const isLoading = isEditMode ? updateBillLoading : createBillLoading;
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? `Edit Bill ${editBill?.billNumber ?? ''}` : 'Create Bill'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        {isEditMode && editBill?.status === 'PAID' && (
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle me-2" />
            This bill has been paid. Some fields may not be editable.
          </Alert>
        )}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Supplier</Form.Label>
            {isEditMode ? (
              <Form.Control type="text" value={editBill?.contactName ?? '—'} disabled className="bg-secondary text-muted" />
            ) : (
              <Form.Select
                value={selectedContactId}
                onChange={e => setSelectedContactId(e.target.value)}
                aria-label="Select supplier"
              >
                <option value="">Select a supplier</option>
                {customerOptions.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.company || c.id}
                    {c.email ? ` (${c.email})` : ''}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Bill Date</Form.Label>
                <Form.Control type="date" value={billDate} onChange={e => setBillDate(e.target.value)} />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Due Date</Form.Label>
                <Form.Control type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Reference (optional)</Form.Label>
            <Form.Control
              type="text"
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder="e.g. Supplier invoice number"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Line Items</Form.Label>
            <Table bordered size="sm" className="mb-2">
              <thead>
                <tr>
                  <th style={{ width: '50%' }}>Description</th>
                  <th style={{ width: '15%' }}>Qty</th>
                  <th style={{ width: '20%' }}>Unit Price</th>
                  <th style={{ width: '15%' }}>Amount</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map(item => {
                  const qty = parseFloat(item.quantity) || 0;
                  const unit = parseFloat(item.unitAmount) || 0;
                  const amount = qty * unit;
                  return (
                    <tr key={item.id}>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={item.description}
                          onChange={e => updateLineItem(item.id, 'description', e.target.value)}
                          placeholder="Description"
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          step="0.01"
                          value={item.quantity}
                          onChange={e => updateLineItem(item.id, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          value={item.unitAmount}
                          onChange={e => updateLineItem(item.id, 'unitAmount', e.target.value)}
                          placeholder="0.00"
                        />
                      </td>
                      <td className="align-middle text-end">{formatCurrency(amount)}</td>
                      <td className="align-middle text-center">
                        {lineItems.length > 1 && (
                          <Button variant="link" size="sm" className="text-danger p-0" onClick={() => removeLineItem(item.id)}>
                            <i className="bi bi-trash" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {(() => {
                  const t = computeTotals();
                  return (
                    <>
                      <tr>
                        <td colSpan={3} className="text-end text-muted">Subtotal</td>
                        <td className="text-end text-muted">{formatCurrency(t.subtotal)}</td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan={3} className="text-end text-muted">GST (10%)</td>
                        <td className="text-end text-muted">{formatCurrency(t.gst)}</td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan={3} className="text-end fw-bold">Total</td>
                        <td className="fw-bold text-end">{formatCurrency(t.total)}</td>
                        <td></td>
                      </tr>
                    </>
                  );
                })()}
              </tfoot>
            </Table>
            <Button variant="outline-primary" size="sm" onClick={addLineItem}>
              <i className="bi bi-plus-lg me-1" /> Add Line Item
            </Button>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? <><span className="spinner-border spinner-border-sm me-2" />{isEditMode ? 'Updating…' : 'Creating…'}</> : (isEditMode ? 'Update Bill' : 'Create Bill')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CreateBillModal;
